import useStore from 'Hooks/useStore';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { trackSelectTrip } from 'Util/_HumanWritten/Analytics/analyticsEvents';
import {
	analyticsBookingType,
	analyticsDepartureTime,
	analyticsRouteLabelFromStops,
	analyticsSailingDate,
} from 'Util/_HumanWritten/Analytics/analyticsBookingFields';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';

export const useLegOptionClick = (
	legType: 'departure' | 'return',
	wizardData: BookingWizardData,
	trip: TicketsTabTrip,
	isSelectable: boolean,
	saveChanges: (newData: BookingWizardData) => void,
) => {
	const store = useStore();

	const { id: tripId, startStopId, endStopId } = trip;

	/**
	 * @param base Wizard data to select the trip on top of, for a caller that has just changed
	 * something else and has not been re-rendered with it yet.
	 *
	 * <p>Without it, two changes in one click lose each other: this hook closes over the
	 * `wizardData` of the render it was created in, so a caller that saves a change and then calls
	 * this one is overwritten by a copy taken before that change existed. The fare cells do exactly
	 * that — choosing a class and selecting the sailing are one act to a booker — and the class was
	 * being wiped by the selection that followed it.</p>
	 *
	 * <p>Guarded rather than typed, because `onClick` is handed straight to DOM handlers in
	 * `LegOptionContainer` and would otherwise pass a `MouseEvent` in here.</p>
	 */
	const selectTicket = (base?: unknown) => {
		const from = base instanceof BookingWizardData ? base : wizardData;
		const newData = new BookingWizardData({ ...from });
		// `isSelectable` already lets through what the caller's capabilities allow (useIsLegSelectable).
		// This used to let any staff member click past it, which re-opened departed and full sailings
		// to someone whose OverrideBookingRestrictions or OverbookInBookingWizard had been revoked.
		if (!isSelectable) {
			return;
		}

		const { departureTrip, departingJourney, returningJourney, departureTicketId, returningTicketId } = newData;

		// Conditions:
		// - for departing ferry trips
		//   - if id is empty, then assign new ticket
		//   - if id is NOT empty, make id empty
		// - for returning ferry trips
		//   - if id is empty, then assign new ticket
		//   - if id is NOT empty, make id empty

		// Tracks whether this click selected a trip (vs deselected one) so we only fire the
		// `select_item` analytics event on selection.
		let didSelect = false;

		if (legType === 'departure') {
			// Select or deselect departure ticket
			if (departureTrip === true || departureTrip === undefined) {
				if (departingJourney || departureTicketId) {
					newData.departingJourney = undefined;
					newData.departureTicketId = '';
				} else {
					newData.departingJourney = { tripId, startStopId, endStopId };
					newData.departureTicketId = trip.id;
					newData.ticketSelectionStartDate = trip.departureDateTime;
					didSelect = true;
				}
			}

			saveChanges(newData);
			if (didSelect) {
				trackSelectTrip({
					leg: 'departure',
					trip_id: tripId,
					booking_type: analyticsBookingType(wizardData.tabSelected),
					route: analyticsRouteLabelFromStops(startStopId, endStopId),
					sailing_date: analyticsSailingDate(trip.departureDateTime),
					departure_time: analyticsDepartureTime(trip.departureDateTime),
				});
			}
			return;
		}

		if (legType === 'return') {
			if (departureTrip === false) {
				// False means we are editing only the return leg of a booking,
				// and we are storing it in the departureTicketId (departingJourney)
				if (departingJourney || departureTicketId) {
					newData.departingJourney = undefined;
					newData.departureTicketId = '';
				} else {
					newData.departingJourney = { tripId, startStopId, endStopId };
					newData.departureTicketId = trip.id;
					newData.ticketSelectionEndDate = trip.departureDateTime;
					didSelect = true;
				}
			}
			if (departureTrip === undefined) {
				if (returningJourney || returningTicketId) {
					newData.returningJourney = undefined;
					newData.returningTicketId = '';
				} else {
					newData.returningJourney = { tripId, startStopId, endStopId };
					newData.returningTicketId = trip.id;
					newData.ticketSelectionEndDate = trip.departureDateTime;
					didSelect = true;
				}
			}
		}

		saveChanges(newData);
		if (didSelect) {
			trackSelectTrip({
				leg: 'return',
				trip_id: tripId,
				booking_type: analyticsBookingType(wizardData.tabSelected),
				route: analyticsRouteLabelFromStops(startStopId, endStopId),
				sailing_date: analyticsSailingDate(trip.departureDateTime),
				departure_time: analyticsDepartureTime(trip.departureDateTime),
			});
		}

		// if (departureTicketId === '' && (departure || (wizardMode === 'ALTERATION' && departureTrip !== false))) {
		// 	newData.departureTicketId = trip.id;
		// 	// Set the departure date to the selected ferry ticket date and set the destination date if it is before
		// 	// the selected tickets departure date
		// 	unselectInvalidDateTicket(newData);
		// } else if (departureTicketId === '' && wizardMode === 'ALTERATION' && departureTrip === false) {
		// 	// This is used for when the user passes the return ticket of a two-way trip into the wizard
		// 	// Because this scenrio will not be able to enter the above IF statement
		// 	newData.departureTicketId = trip.id;

		// 	// Set the departure date to the selected ferry ticket date
		// 	newData.endDate = trip.startDate;
		// } else if (!!departureTicketId && (departure || (wizardMode === 'ALTERATION' && departureTrip === false))) {
		// 	newData.ticketSelectionStartDate = newData.startDate;
		// 	newData.departureTicketId = '';
		// } else if (!departure && returningTicketId === '') {
		// 	newData.returningTicketId = trip.id;
		// } else if (!departure && returningTicketId !== '' && returningTicketId !== undefined) {
		// 	newData.ticketSelectionEndDate = newData.endDate;
		// 	newData.returningTicketId = '';
		// }

		// saveChanges(newData);
	};

	return selectTicket;
};
